import type * as Stitches from '@stitches/react'
import { createStitches } from '@stitches/react'

const COLORS = {
  // white transparent
  whiteA025: '#FFFFFF06',
  whiteA050: '#FFFFFF0D',
  whiteA100: '#FFFFFF1A',
  whiteA150: '#FFFFFF26',
  whiteA200: '#FFFFFF33',
  whiteA300: '#FFFFFF4D',
  whiteA400: '#FFFFFF66',
  whiteA500: '#FFFFFF80',
  whiteA600: '#FFFFFF99',
  whiteA700: '#FFFFFFB2',
  whiteA800: '#FFFFFFCC',
  whiteA900: '#FFFFFFE5',
  // white
  white: '#FFFFFF',
  // black transparent
  blackA025: '#00000006',
  blackA050: '#0000000D',
  blackA100: '#0000001A',
  blackA150: '#00000026',
  blackA200: '#00000033',
  blackA300: '#0000004D',
  blackA400: '#00000066',
  blackA500: '#00000080',
  blackA600: '#00000099',
  blackA700: '#000000B2',
  blackA800: '#000000CC',
  blackA900: '#000000E5',
  // black
  black: '#000000',
  // neutrals
  gray0100: '#FEFEFE',
  gray0200: '#FCFCFC',
  gray0300: '#F3F3F3',
  gray0400: '#E2E2E2',
  gray0500: '#C6C6C6',
  gray0600: '#B2B2B2',
  gray0700: '#303030',
  gray0800: '#1B1B1B',
  gray0900: '#0F0F0F',
  gray1000: '#040404',
  // pink
  pink0100: '#FDF4FD',
  pink0200: '#FAE9FB',
  pink0300: '#F5D0F6',
  pink0400: '#F0B4F1',
  pink0500: '#EA93ED',
  pink0600: '#E567E8',
  pink0700: '#CD5CD0',
  pink0800: '#B150B4',
  pink0900: '#914193',
  pink1000: '#662E68',
  // purple
  purple0100: '#F8F5FF',
  purple0200: '#F2EAFF',
  purple0300: '#E4D4FE',
  purple0400: '#D5BAFE',
  purple0500: '#C59DFD',
  purple0600: '#B378FD',
  purple0700: '#A06BE2',
  purple0800: '#8B5DC4',
  purple0900: '#714CA0',
  purple1000: '#503671',
  // indigo
  indigo0100: '#F5F5FF',
  indigo0200: '#EAEBFE',
  indigo0300: '#D4D5FD',
  indigo0400: '#BABCFC',
  indigo0500: '#9DA0FB',
  indigo0600: '#787DFA',
  indigo0700: '#6B70E0',
  indigo0800: '#5D61C2',
  indigo0900: '#4C4F9E',
  indigo1000: '#363870',
  // blue
  blue0100: '#F4F6FE',
  blue0200: '#E7EDFD',
  blue0300: '#CDD9FB',
  blue0400: '#AFC3F8',
  blue0500: '#8BAAF6',
  blue0600: '#588DF4',
  blue0700: '#4F7EDA',
  blue0800: '#446DBD',
  blue0900: '#38599A',
  blue1000: '#273F6D',
  // light blue
  lightblue0100: '#F4F8FE',
  lightblue0200: '#E8F1FE',
  lightblue0300: '#CEE2FD',
  lightblue0400: '#B1D2FB',
  lightblue0500: '#8DC1FA',
  lightblue0600: '#5DAEF9',
  lightblue0700: '#539CDF',
  lightblue0800: '#4887C1',
  lightblue0900: '#3B6E9D',
  lightblue1000: '#2A4E6F',
  // teal
  teal0100: '#F3FBF9',
  teal0200: '#E6F6F3',
  teal0300: '#CBEEE6',
  teal0400: '#ABE5D8',
  teal0500: '#84DBC9',
  teal0600: '#4AD1B9',
  teal0700: '#42BBA5',
  teal0800: '#39A28F',
  teal0900: '#2F8475',
  teal1000: '#215D53',
  // green
  green0100: '#F4FBF4',
  green0200: '#E8F6E8',
  green0300: '#CFEECE',
  green0400: '#B2E5B1',
  green0500: '#90DB8D',
  green0600: '#62D15D',
  green0700: '#58BB53',
  green0800: '#4CA248',
  green0900: '#3E843B',
  green1000: '#2C5D2A',
  // yellow
  yellow0100: '#FDFAF3',
  yellow0200: '#FBF5E7',
  yellow0300: '#F8EBCB',
  yellow0400: '#F4E0AB',
  yellow0500: '#F0D584',
  yellow0600: '#ECC94B',
  yellow0700: '#D3B443',
  yellow0800: '#B79C3A',
  yellow0900: '#957F2F',
  yellow1000: '#6A5A22',
  // orange
  orange0100: '#FEF7F2',
  orange0200: '#FDEEE5',
  orange0300: '#FBDCC8',
  orange0400: '#F8C9A6',
  orange0500: '#F6B37A',
  orange0600: '#F49A30',
  orange0700: '#DA8A2B',
  orange0800: '#BD7725',
  orange0900: '#9A611E',
  orange1000: '#6D4515',
  // deep orange
  deeporange0100: '#FEF6F3',
  deeporange0200: '#FDECE7',
  deeporange0300: '#FBD7CD',
  deeporange0400: '#F9C0AF',
  deeporange0500: '#F7A58A',
  deeporange0600: '#F58657',
  deeporange0700: '#DB784E',
  deeporange0800: '#BE6843',
  deeporange0900: '#9B5537',
  deeporange1000: '#6E3C27',
  // red
  red0100: '#FEF3F3',
  red0200: '#FBE7E7',
  red0300: '#F9CCCC',
  red0400: '#F6ACAC',
  red0500: '#F38586',
  red0600: '#F04F4F',
  red0700: '#D74747',
  red0800: '#BA3D3D',
  red0900: '#983232',
  red1000: '#6B2323',

  // Kits AI
  aiBackground1: '#0C0C0C',
  aiBackground2: '#161616',
  textPrimary: '$whiteA900',
  textSecondary: '$whiteA600'
}

export const BREAKPOINTS = {
  mobile: 540,
  tablet: 768,
  desktop: 1032,
  desktopLarge: 1280
} as const

const DMSans = 'DM Sans, -apple-system, BlinkMacSystemFont, sans-serif'
const DMMono = 'DM Mono, monospace'
const Poppins = 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
const AzeretMono = 'Azeret Mono'

const FONTS = {
  DMSans,
  Poppins,
  DMMono,
  AzeretMono
}

const FONT_WEIGHTS = {
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700'
}

export const BORDER_RADIUS = {
  round: '9999px',
  4: '4px',
  6: '6px',
  8: '8px',
  10: '10px',
  12: '12px',
  16: '16px',
  32: '32px'
}

const textStyles = {
  '60': {
    fontSize: '60px',
    lineHeight: '72px',
    letterSpacing: '-0.04em'
  },
  '48': {
    fontSize: '48px',
    lineHeight: '56px',
    letterSpacing: '-0.03em'
  },
  '40': {
    fontSize: '40px',
    lineHeight: '48px',
    letterSpacing: '-0.03em'
  },
  '36': {
    fontSize: '36px',
    lineHeight: '44px',
    letterSpacing: '-0.03em'
  },
  '32': {
    fontSize: '32px',
    lineHeight: '44px',
    letterSpacing: '-0.03em'
  },
  '28': {
    fontSize: '28px',
    lineHeight: '40px',
    letterSpacing: '-0.03em'
  },
  '24': {
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '-0.03em'
  },
  '20': {
    fontSize: '20px',
    lineHeight: '28px',
    letterSpacing: '-0.01em'
  },
  '18': {
    fontSize: '18px',
    lineHeight: '24px',
    letterSpacing: '-0.01em'
  },
  '16': {
    fontSize: '16px',
    lineHeight: '20px'
  },
  '14': {
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.01em'
  },
  '12': {
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.02em'
  }
}

export const { styled, css, globalCss, keyframes, theme, createTheme, getCssText, config } =
  createStitches({
    prefix: 'ui',
    media: {
      mobile: `(min-width: ${BREAKPOINTS.mobile}px)`,
      tablet: `(min-width: ${BREAKPOINTS.tablet}px)`,
      desktop: `(min-width: ${BREAKPOINTS.desktop}px)`,
      desktopLarge: `(min-width: ${BREAKPOINTS.desktopLarge}px)`
    } as {}, // important config change, ingore type of media keys to stop freezes of intellisense
    theme: {
      fonts: FONTS,
      fontWeights: FONT_WEIGHTS,
      colors: COLORS,
      fontSizes: {
        '12': '12px',
        '14': '14px',
        '16': '16px',
        '18': '18px',
        '20': '20px',
        '24': '24px',
        '28': '28px',
        '32': '32px',
        '36': '36px',
        '40': '40px',
        '48': '48px',
        '50': '50px'
      },
      lineHeights: {},
      radii: BORDER_RADIUS
    },
    utils: {
      text: (value: keyof typeof textStyles) => textStyles[value],
      textFamily: (value: 'dm-bold') => {
        switch (value) {
          case 'dm-bold':
            return {
              fontFamily: 'DM Sans',
              fontWeight: 700
            }
          default:
            return {}
        }
      }
    }
  })

export type CSS = Stitches.CSS<typeof config>
